import React from 'react'
import { motion } from 'framer-motion'
import { Bar } from 'react-chartjs-2'
import { Chart as ChartJS, BarElement, CategoryScale, LinearScale, Tooltip, Legend } from 'chart.js'
import { format } from 'date-fns'

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend)

const HoldingRewardsResult = ({ result }) => {
  if (!result) return null

  const data = result
  const formattedDate = format(new Date(data.timestamp), 'PPpp')

  const chartData = {
    labels: ['Initial Value', 'Current Value', 'Profit/Loss'],
    datasets: [
      {
        label: 'USD ($)',
        data: [data.initial_value, data.current_value, data.profit_loss],
        backgroundColor: ['#60a5fa', '#22c55e', data.profit_loss >= 0 ? '#16a34a' : '#ef4444'],
        borderRadius: 10,
        barThickness: 30,
      },
    ],
  }

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: ctx => `$${ctx.raw.toFixed(2)}`
        }
      }
    },
    scales: {
      y: {
        ticks: {
          callback: val => `$${val}`,
          color: '#fff'
        },
        grid: { color: 'rgba(255,255,255,0.1)' }
      },
      x: {
        ticks: { color: '#fff' },
        grid: { display: false }
      }
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="rounded-2xl p-6 shadow-xl  bg-slate-800/50  text-white backdrop-blur-lg border border-white/10"
    >
      <h2 className="text-2xl font-bold mb-4">Holding Rewards Result</h2>
      <p className="text-sm text-gray-300 mb-6">Data reflects the most recent update as of:: {formattedDate}</p>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        <InfoBlock label="Exchange" value={data.exchange} />
        <InfoBlock label="Symbol" value={data.symbol} />
        <InfoBlock label="Quantity" value={data.quantity} />
        <InfoBlock label="Entry Price" value={`$${data.entry_price.toFixed(2)}`} />
        <InfoBlock label="Current Price" value={`$${data.current_price.toFixed(2)}`} />
      </div>

      <div className="mt-6 mb-8">
        <h3 className="text-lg font-semibold mb-3">Value Summary</h3>
        <table className="min-w-full text-sm text-left bg-zinc-800/50 border border-white/10 rounded-xl overflow-hidden">
          <thead className="bg-zinc-700/50 text-gray-300">
            <tr>
              <th className="px-4 py-2">Metric</th>
              <th className="px-4 py-2">USD ($)</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-t border-zinc-700">
              <td className="px-4 py-2">Initial Value</td>
              <td className="px-4 py-2">${data.initial_value.toFixed(2)}</td>
            </tr>
            <tr className="border-t border-zinc-700">
              <td className="px-4 py-2">Current Value</td>
              <td className="px-4 py-2">${data.current_value.toFixed(2)}</td>
            </tr>
            <tr className="border-t border-zinc-700">
              <td className="px-4 py-2 font-semibold text-green-400">Profit / Loss</td>
              <td className="px-4 py-2 font-semibold text-green-400">${data.profit_loss.toFixed(2)}</td>
            </tr>
            <tr className="border-t border-zinc-700">
              <td className="px-4 py-2">Profit %</td>
              <td className="px-4 py-2 text-yellow-300 font-semibold">{data.profit_loss_pct.toFixed(2)}%</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="mt-6">
        <h3 className="text-lg font-semibold mb-2">Growth Visualization</h3>
        <Bar data={chartData} options={chartOptions} />
      </div>
      <p className="text-xs text-gray-300 mb-6">This is tentative data and may not reflect precise values. </p>

    </motion.div>
  )
}

const InfoBlock = ({ label, value }) => (
  <div>
    <p className="text-sm text-gray-400">{label}</p>
    <p className="font-semibold">{value}</p>
  </div>
)

export default HoldingRewardsResult
